import { useCallback } from 'react';
import { useLocation, useNavigate, type To } from 'react-router-dom';
import { markAppBackTransition } from '../components/layout/routeTransitionIntent';

export type AppBackState = {
  appBack?: true;
  backTo?: To;
};

export function routeBackState(backTo?: To): AppBackState {
  return backTo ? { appBack: true, backTo } : { appBack: true };
}

function hasAppBackState(value: unknown): value is AppBackState {
  return Boolean(value && typeof value === 'object' && 'appBack' in value);
}

export function useBackOrFallback(fallback: To) {
  const location = useLocation();
  const navigate = useNavigate();

  return useCallback(() => {
    const state = location.state;
    if (hasAppBackState(state)) {
      const historyIndex = typeof window !== 'undefined' ? window.history.state?.idx : undefined;
      if (typeof historyIndex === 'number' && historyIndex > 0) {
        markAppBackTransition();
        navigate(-1);
        return;
      }

      markAppBackTransition();
      navigate(state.backTo ?? fallback, { replace: true });
      return;
    }

    markAppBackTransition();
    navigate(fallback, { replace: true });
  }, [fallback, location.state, navigate]);
}
